/**
 * Share / copy actions for a listing (moved from ListingDetailPage.tsx): the consolidated "Share
 * listing" dialog (public link, intake email, QR code), the intake-email draft/pending convention,
 * and a labelled copy box used by the Overview and the go-live notice.
 */

import { useState, useRef, useEffect } from 'react';
import { Copy, Check, Share2 } from 'lucide-react';
import QRCode from 'qrcode';
import { useCopyToClipboard } from '../../../hooks/useCopyToClipboard';
import { Button } from '../../../components/ui/button';
import { Input } from '../../../components/ui/input';
import { Textarea } from '../../../components/ui/textarea';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogTrigger,
} from '../../../components/ui/dialog';
import type { Listing } from '../../../lib/leasing.api';

// ---------------------------------------------------------------------------
// Section: Copy Listing Link Button (renamed from CopyPublicLinkButton)
// ---------------------------------------------------------------------------

interface CopyListingLinkButtonProps {
  listing: Listing;
}

function CopyListingLinkButton({ listing }: CopyListingLinkButtonProps) {
  const { copied, copyFailed, copy } = useCopyToClipboard();
  const urlRef = useRef<HTMLInputElement | null>(null);

  // Same 50ms-then-select behavior as before the refactor — reacts to copyFailed
  // (set by the hook) instead of running inline inside the catch block.
  useEffect(() => {
    if (!copyFailed) return;
    const t = setTimeout(() => urlRef.current?.select(), 50);
    return () => clearTimeout(t);
  }, [copyFailed]);

  if (!listing.publicToken || !listing.publicUrl) return null;

  return (
    <div className="flex flex-col gap-1.5 items-start">
      <Button
        type="button"
        variant="outline"
        size="sm"
        className="min-h-[44px]"
        data-testid="copy-listing-link-button"
        onClick={() => void copy(listing.publicUrl!)}
        aria-label={copied ? 'Listing link copied' : 'Copy listing link'}
      >
        {copied ? (
          <>
            <Check className="h-3.5 w-3.5 mr-1.5" aria-hidden="true" />
            Copied!
          </>
        ) : (
          <>
            <Copy className="h-3.5 w-3.5 mr-1.5" aria-hidden="true" />
            Copy Listing Link
          </>
        )}
      </Button>
      {copyFailed && (
        <div className="flex flex-col gap-1" role="alert" aria-live="polite">
          <p className="text-xs text-muted-foreground">
            Clipboard unavailable — copy the link below:
          </p>
          <Input
            ref={urlRef}
            data-testid="copy-listing-link-fallback"
            type="text"
            readOnly
            value={listing.publicUrl}
            className="border-border px-2 py-1 text-foreground md:text-xs"
            onFocus={(e) => e.target.select()}
          />
        </div>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Section: Copy Listing Email Button (replaces IntakeAddressBanner)
// ---------------------------------------------------------------------------

/**
 * Shared draft/pending convention for the intake email address: not minted until the
 * listing is first activated, and briefly pending right after activation. Used by both
 * CopyListingEmailButton, ContactInfoEditor and the draft setup preview so their empty states stay in sync.
 */
export function getIntakeEmailState(
  listing: Listing,
  intakeAddress: string | null
): { isDraft: boolean; isPending: boolean } {
  const isDraft = listing.status === 'draft';
  return { isDraft, isPending: !isDraft && !intakeAddress };
}

interface CopyListingEmailButtonProps {
  listing: Listing;
  intakeAddress: string | null;
}

function CopyListingEmailButton({ listing, intakeAddress }: CopyListingEmailButtonProps) {
  const { copied, copy } = useCopyToClipboard();

  // Matches IntakeAddressBanner's prior closed-status behavior: render nothing.
  if (listing.status === 'closed') return null;

  const { isDraft, isPending } = getIntakeEmailState(listing, intakeAddress);
  const disabled = isDraft || isPending;

  const caption = isDraft
    ? null
    : isPending
      ? 'Address pending…'
      : listing.status === 'paused'
        ? 'Paused — new inbound will receive your on-hold auto-reply.'
        : 'Use this as your contact email on rental listing sites or your own listing.';

  return (
    <div className="flex flex-col gap-1 items-start">
      <Button
        type="button"
        variant="outline"
        size="sm"
        className="min-h-[44px]"
        data-testid="copy-listing-email-button"
        onClick={() => intakeAddress && void copy(intakeAddress)}
        disabled={disabled}
        aria-disabled={disabled}
        title={isDraft ? 'Activate this listing to get your intake email address.' : undefined}
        aria-label={copied ? 'Listing email copied' : 'Copy listing email'}
      >
        {copied ? (
          <>
            <Check className="h-3.5 w-3.5 mr-1.5" aria-hidden="true" />
            Copied!
          </>
        ) : (
          <>
            <Copy className="h-3.5 w-3.5 mr-1.5" aria-hidden="true" />
            Copy Listing Email
          </>
        )}
      </Button>
      {caption && (
        <p
          className={`text-xs max-w-[16rem] ${
            listing.status === 'paused' ? 'text-warning' : 'text-muted-foreground'
          }`}
        >
          {caption}
        </p>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Section: Share Listing (link + email + QR code consolidated behind one
// trigger — listing detail action-bar reorg, see docs/claude change notes)
// ---------------------------------------------------------------------------

interface ShareListingButtonProps {
  listing: Listing;
  intakeAddress: string | null;
}

export function ShareListingButton({ listing, intakeAddress }: ShareListingButtonProps) {
  const [open, setOpen] = useState(false);
  const [qrDataUrl, setQrDataUrl] = useState<string | null>(null);
  const [qrError, setQrError] = useState<string | null>(null);

  const handleOpenChange = (next: boolean) => {
    setOpen(next);
    if (!next || qrDataUrl || !listing.publicUrl) return;
    setQrError(null);
    QRCode.toDataURL(listing.publicUrl)
      .then((url) => setQrDataUrl(url))
      .catch((err: unknown) => {
        setQrError(err instanceof Error ? err.message : 'Failed to generate QR code');
      });
  };

  const hasLink = !!(listing.publicToken && listing.publicUrl);

  // Link/QR need a minted publicToken (BR-LSI6-02), but the Copy Email action is
  // still meaningful (visible-but-disabled, with an explanatory tooltip — AC-6) while
  // draft. So the trigger itself only disappears once there is truly nothing to show
  // for any of the three actions: closed, without ever having minted a link.
  if (!hasLink && listing.status === 'closed') return null;

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogTrigger asChild>
        <Button
          type="button"
          variant="outline"
          size="sm"
          className="min-h-[44px]"
          data-testid="share-listing-button"
          aria-label="Share listing link, email, and QR code"
        >
          <Share2 className="h-3.5 w-3.5 mr-1.5" aria-hidden="true" />
          Share listing
        </Button>
      </DialogTrigger>
      <DialogContent className="max-w-sm">
        <DialogHeader>
          <DialogTitle>Share this listing</DialogTitle>
          <DialogDescription>
            Send prospects your public listing link or intake email, or let them scan the QR code.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          <CopyListingLinkButton listing={listing} />
          <CopyListingEmailButton listing={listing} intakeAddress={intakeAddress} />

          {hasLink && (
          <div>
            <span className="block text-sm font-medium mb-2">QR code</span>
            {qrError && (
              <p className="text-sm text-destructive" role="alert" data-testid="qr-code-error">
                {qrError}
              </p>
            )}
            {!qrError && !qrDataUrl && (
              <p className="text-sm text-muted-foreground">Generating…</p>
            )}
            {qrDataUrl && (
              <img
                src={qrDataUrl}
                alt="QR code linking to the public listing page"
                data-testid="qr-code-image"
                className="h-40 w-40"
              />
            )}
          </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}

// ---------------------------------------------------------------------------
// Copy box: a value (intake email / public link / marketing text) with its own Copy action
// ---------------------------------------------------------------------------

interface CopyValueBoxProps {
  label: string;
  /** null while the value is not available yet (e.g. the intake address right after publish). */
  value: string | null;
  emptyText: string;
  testIdPrefix: string;
  caption?: string;
  /** A multi-line value (a listing description, a canned reply): line breaks kept, Copy below it. */
  multiline?: boolean;
  /** The Copy button's text (default "Copy"), e.g. "Copy description". */
  copyLabel?: string;
}

export function CopyValueBox({
  label,
  value,
  emptyText,
  testIdPrefix,
  caption,
  multiline = false,
  copyLabel = 'Copy',
}: CopyValueBoxProps) {
  const { copied, copyFailed, copy } = useCopyToClipboard();

  return (
    <div className="flex flex-col gap-1.5 min-w-0" data-testid={`${testIdPrefix}-box`}>
      <span className="text-sm font-medium text-foreground">{label}</span>
      <div
        className={`flex min-w-0 gap-2 rounded-md border border-border bg-muted/40 ${
          multiline ? 'flex-col items-start p-3' : 'items-center pl-3 pr-1 py-1'
        }`}
      >
        <span
          className={`min-w-0 text-sm ${multiline ? 'w-full whitespace-pre-wrap break-words' : 'flex-1 break-all'} ${
            value ? 'text-foreground' : 'text-muted-foreground'
          }`}
          data-testid={`${testIdPrefix}-value`}
          title={value ?? undefined}
        >
          {value ?? emptyText}
        </span>
        {value && (
          <Button
            type="button"
            variant="outline"
            size="sm"
            className="min-h-[44px] shrink-0"
            data-testid={`${testIdPrefix}-copy`}
            onClick={() => void copy(value)}
            aria-label={copied ? `${label} copied` : `Copy ${label.toLowerCase()}`}
          >
            {copied ? (
              <Check className="h-3.5 w-3.5" aria-hidden="true" />
            ) : (
              <Copy className="h-3.5 w-3.5" aria-hidden="true" />
            )}
            <span className="ml-1.5">{copied ? 'Copied' : copyLabel}</span>
          </Button>
        )}
      </div>
      {copyFailed && value && (
        <div className="flex flex-col gap-1" role="alert">
          <p className="text-xs text-muted-foreground">Clipboard unavailable. Copy it from here:</p>
          {multiline ? (
            <Textarea
              readOnly
              value={value}
              aria-label={label}
              rows={6}
              data-testid={`${testIdPrefix}-fallback`}
              className="border-border text-foreground md:text-xs"
              onFocus={(e) => e.target.select()}
            />
          ) : (
            <Input
              type="text"
              readOnly
              value={value}
              aria-label={label}
              data-testid={`${testIdPrefix}-fallback`}
              className="min-h-[44px] border-border text-foreground md:text-xs"
              onFocus={(e) => e.target.select()}
            />
          )}
        </div>
      )}
      {caption && <p className="text-xs text-muted-foreground">{caption}</p>}
    </div>
  );
}
